import React from 'react'
import Helmet from 'react-helmet'
import cx from 'classnames'
import links from 'misc/links'
import intercomUpdate from 'decorators/intercomUpdate'
import { observer } from 'mobx-react'
import { match } from 'utils/patternMatchUtil'
import { MessageComposer } from 'components/MessageComposer'
import { HeaderSink, HeaderTitle } from 'components/Header'
import { Timeline, TimelineEvent } from 'components/Timeline'
import FileButton from 'components/FileButton'
import Link from 'components/Link'
import ConversationContainer from 'containers/ConversationContainer'
import ProgressSpinner from 'components/ProgressSpinner'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import { FlexContainer, FlexItem } from 'components/Flex'
import PaddedBlock from 'components/PaddedBlock'
import { MiniTabs, MiniTab } from 'components/MiniTabs'
import Document from 'components/Document'
import JobInfo from './JobInfo'
import Amendments from './Amendments'
import Events from './Events'

import avatarPlaceholder from 'assets/img/avatar-placeholder.png'
import './styles.sass'

@intercomUpdate({ screen: 'Project Details' })
@observer
export default class ProjectDetails extends React.Component {
  constructor () {
    super(...arguments)
    this.store = this.props.rootStore.projectDetailsStore
  }

  componentWillMount () {
    const id = this.props.params.id
    this.store.activate(id)
  }

  componentWillUnmount () {
    this.store.deactivate()
  }

  renderLoader () {
    return (
      <FlexContainer
        alignItems="center"
        justifyContent="center"
        className="is-h100"
      >
        <FlexItem>
          <ProgressSpinner blue />
        </FlexItem>
      </FlexContainer>
    )
  }

  renderMilestones () {
    return (
      <Timeline>
        {_generateTimeline(this.store.project.timeline)}
      </Timeline>
    )
  }

  renderConversation () {
    return (
      this.store.messages.length === 0
        ? this.renderConversationPlaceholder()
        : (
          <ConversationContainer
            messages={this.store.messages}
            unreadMessagesCount={this.store.unreadMessagesCount}
            otherName="Pro"
            onMarkAsRead={(message) => this.props.rootStore.messageStore.markAsRead(message)}
            onDocumentClick={(message) => this.props.rootStore.documentStore.download(message.document)}
            me={this.props.rootStore.authStore.user}
            {...this.store.conversation.hasMoreMessages && {
              isLoadingMore: this.store.conversation.loadingMore,
              onLoadMore: () => this.props.rootStore.messageStore.loadMore(this.store.project.conversationId)
            }}
          />
        )
    )
  }

  renderConversationPlaceholder () {
    return (
      <div
        className="conversation-placeholder has-text-centered is-h100"
      >
        <PaddedBlock>
          <img src={avatarPlaceholder} />
        </PaddedBlock>
        <h3 className="title is-3 is-semibold">
          Hello!
        </h3>
        <div className="content has-hspacing">
          <p>
            A Pro will be with you shortly. While you wait, we suggest
            you upload whatever documents your Pro might need if you haven't done so already.
          </p>
        </div>
      </div>
    )
  }

  renderComposer () {
    if (this.store.project.completed) {
      return false
    }

    return (
      <MessageComposer
        value={this.store.composerText}
        onChange={this.store.changeComposerText}
        onSend={this.store.sendMessage}
        disabled={this.store.loadingMessages}
      />
    )
  }

  renderNoDocuments () {
    return (
      <PaddedBlock className="no-documents has-text-centered">
        <img src={require('assets/icons/docs.png')} alt="No Documents"/>
        <div className="content has-vspacing">
          <p>
            No documents have been uploaded yet.
          </p>
        </div>
      </PaddedBlock>
    )
  }

  renderDocuments () {
    const documents = this.store.project.documents
    if (this.props.rootStore.documentStore.loading) {
      return this.renderLoader()
    }

    if (documents.length === 0) {
      return this.renderNoDocuments()
    }

    return this.store.project.documents.map((d) =>
      <div className="has-spacing-bottom" key={d.cid}>
        <Document canChangeCategory document={d} onEditClick={() => this.store.editDocument(d)} />
      </div>
    )
  }

  renderDetails () {
    if (!this.store.project) {
      return null
    }
    return match(this.store.detailsTab, {
      INFO: () => (
        <JobInfo job={this.store.project} />
      ),
      AMENDMENTS: () => (
        <Amendments amendments={this.store.project.amendments} />
      ),
      EVENTS: () => (
        <Events events={this.store.project.events} />
      )
    })
  }

  renderColumns () {
    const project = this.store.project
    const hasProvider = !!project.provider

    const documentsColumn = (
      <FlexContainer alignItems="flex-start" direction="column" className="column documents-column is-narrow">
        <FlexItem className="documents-column-header is-column-header is-w100">
          <SectionTitle>Documents</SectionTitle>
        </FlexItem>

        <FlexItem className="documents-container is-scrollable is-w100" stretch>
          <div className="documents-actions">
            {!this.props.rootStore.documentStore.loading &&
              <FileButton onChange={(files) => this.store.stageFiles(files)}>
                <a className="button is-medium is-document-action">
                  <span className="icon">
                    <Icon material name="cloud upload" />
                  </span>
                  <span>
                    Upload Document
                  </span>
                </a>
              </FileButton>
            }
            {/* &nbsp; */}
            {/* &nbsp; */}
            {/* <Link */}
              {/* className="button is-medium is-document-action" */}
              {/* to={links.linkedAccounts()} */}
            {/* > */}
              {/* <span className="icon"> */}
                {/* <Icon material name="account balance" /> */}
              {/* </span> */}
              {/* <span> */}
                {/* Link Account */}
              {/* </span> */}
            {/* </Link> */}
          </div>

          {this.renderDocuments()}
        </FlexItem>
      </FlexContainer>
    )

    const messagesColumn = (
      <FlexContainer alignItems="flex-start" direction="column" className="column messages-column">
        <FlexItem className="is-column-header is-w100">
          {!hasProvider &&
            <SectionTitle>Messages</SectionTitle>
          }
          {hasProvider &&
            <FlexContainer>
              <FlexItem>
                <Avatar size={28} url={project.provider.avatar} fallback={project.provider.avatarFallback} />
              </FlexItem>
              <FlexItem>
                <SectionTitle>
                  &nbsp;
                  {project.provider.displayName}
                </SectionTitle>
              </FlexItem>
            </FlexContainer>
          }
        </FlexItem>
        <FlexItem className="is-scrollable is-w100 is-h100 messages-container" stretch>
          {this.store.loadingMessages
            ? this.renderLoader()
            : this.renderConversation()
          }
        </FlexItem>
        <FlexItem className="composer-container is-w100">
          {!this.store.loadingMessages && this.store.project.provider && this.renderComposer()}
        </FlexItem>
      </FlexContainer>
    )

    const tab = (tabName) => ({
      active: this.store.detailsTab === tabName,
      onClick: () => this.store.changeDetailsTab(tabName)
    })
    const detailsColumn = (
      <FlexContainer alignItems="flex-start" direction="column" className="details-column column is-narrow">
        <FlexItem className="details-column-header is-column-header is-w100">
          <SectionTitle>Details</SectionTitle>
        </FlexItem>
        <FlexItem className="is-w100">
          <MiniTabs>
            <MiniTab {...tab('INFO')}>Info</MiniTab>
            <MiniTab {...tab('AMENDMENTS')}>Amendments</MiniTab>
            <MiniTab {...tab('EVENTS')}>Events</MiniTab>
          </MiniTabs>
        </FlexItem>
        <FlexItem className="details-container is-scrollable is-w100" stretch>
          {this.renderDetails()}
        </FlexItem>
      </FlexContainer>
    )

    return (
      <div className={cx('columns is-gapless details-columns')}>
        {documentsColumn}
        {messagesColumn}
        {detailsColumn}
      </div>
    )
  }

  renderProjectDetails () {
    const project = this.store.project
    return (
      <div className="project-details-screen animated fadeIn">
        <Helmet title={project.name} />
        <HeaderSink small>
          <HeaderTitle>{project.name}</HeaderTitle>
        </HeaderSink>
        {this.renderColumns()}
      </div>
    )
  }

  renderNotFound () {
    return (
      <div className="project-not-found has-spacing-top has-text-centered content">
        <h1 className="title is-1">Oh, bugger!</h1>
        <p>We couldn't find that project.</p>
        <p>
          <Link to={links.projects()}>
            Go back to Projects overview
          </Link>
        </p>
      </div>
    )
  }

  render () {
    if (this.store.notFound) {
      return this.renderNotFound()
    }

    return !this.store.project
      ? (
        <div style={{ height: '100vh' }}>
          <Helmet title="Taxfyle" />
          {this.renderLoader()}
        </div>
      )
      : this.renderProjectDetails()
  }
}

const SectionTitle = ({ children }) => (
  <h1 className="title is-4 is-semibold">{children}</h1>
)

const _generateTimeline = (timeline) => {
  return timeline.map((milestone) =>
    <TimelineEvent
      key={milestone.text}
      date={milestone.date}
      completed
    >
      {milestone.text}
    </TimelineEvent>
  )
}



// WEBPACK FOOTER //
// ./src/client/screens/ProjectDetails/index.jsx